import { useEffect, useState } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import Loading from '../Loading'
import axiosInstance from '../../utils/axiosInstance'

interface LegacyCampaignRedirectProps {
  children: React.ReactNode
}

/**
 * Older new-lead notification emails linked to the legacy campaign page,
 * /campaigns/:id?lead=:leadId. Those links are sent on to the order's Leads tab, for
 * every role. Links inside the app that mean the legacy page itself carry `?legacy=1`
 * and are left alone. If the link can't be resolved (no order, or not the caller's),
 * the legacy page renders and the server's tenant check decides what it shows.
 */
const LegacyCampaignRedirect: React.FC<LegacyCampaignRedirectProps> = ({ children }) => {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const lead = searchParams.get('lead')
  const isLegacyView = searchParams.has('legacy')
  const [target, setTarget] = useState<string | null>(null)
  const [resolved, setResolved] = useState(isLegacyView)

  useEffect(() => {
    if (isLegacyView || !id) {
      setResolved(true)
      return
    }
    let cancelled = false
    axiosInstance
      .get(`/campaign/${id}/order-link`, { params: lead ? { lead } : {} })
      .then(({ data }) => {
        if (cancelled) return
        setTarget(`/order/${data.orderId}/leads${data.leadId ? `?id=${data.leadId}` : ''}`)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setResolved(true)
      })
    return () => {
      cancelled = true
    }
  }, [id, lead, isLegacyView])

  if (target) return <Navigate to={target} replace />
  if (!resolved) return <Loading />
  return <>{children}</>
}

export default LegacyCampaignRedirect
